import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { courseCategories, courseLevels } from '@/mocks/courses';

export interface CourseRecord {
  id?: string;
  owner_id?: string;
  title: string;
  instructor: string;
  category: string;
  level: string;
  price: number | null;
  duration_hours: number | null;
  lessons_count: number | null;
  language: string;
  description: string;
  objectives: string;
}

const languages = ['Français', 'Anglais', 'Bilingue'];

interface CourseFormProps {
  ownerId: string;
  existing?: CourseRecord | null;
  onSaved: () => void;
}

function emptyForm(): CourseRecord {
  return {
    title: '',
    instructor: '',
    category: courseCategories[0],
    level: courseLevels[0],
    price: null,
    duration_hours: null,
    lessons_count: null,
    language: 'Français',
    description: '',
    objectives: '',
  };
}

function CourseForm({ ownerId, existing, onSaved }: CourseFormProps) {
  const [form, setForm] = useState<CourseRecord>(() => (existing ? { ...existing } : emptyForm()));
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<'idle' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const update = (field: keyof CourseRecord, value: string | number | null) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setStatus('idle');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || !form.instructor.trim()) {
      setStatus('error');
      setErrorMsg('Le titre de la formation et le nom de l\'instructeur sont obligatoires.');
      return;
    }

    setSaving(true);
    setStatus('idle');
    setErrorMsg('');

    const payload = {
      title: form.title.trim(),
      instructor: form.instructor.trim(),
      category: form.category,
      level: form.level,
      price: form.price !== null && form.price >= 0 ? form.price : null,
      duration_hours: form.duration_hours !== null && form.duration_hours > 0 ? form.duration_hours : null,
      lessons_count: form.lessons_count !== null && form.lessons_count > 0 ? form.lessons_count : null,
      language: form.language,
      description: form.description.trim() || null,
      objectives: form.objectives.trim() || null,
    };

    let error = null;
    if (existing?.id) {
      const res = await supabase.from('courses').update(payload).eq('id', existing.id);
      error = res.error;
    } else {
      const res = await supabase.from('courses').insert({ ...payload, owner_id: ownerId });
      error = res.error;
    }

    setSaving(false);

    if (error) {
      setStatus('error');
      setErrorMsg(error.message);
      return;
    }

    onSaved();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <section className="p-6 rounded-xl bg-background-100 border border-background-200/70 space-y-5">
        <h2 className="font-heading font-semibold text-lg text-foreground-50 flex items-center gap-2">
          <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500/10">
            <i className="ri-graduation-cap-line text-primary-400" />
          </span>
          Informations de la formation
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Titre de la formation *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => update('title', e.target.value)}
              placeholder="Ex : Développement Web Full-Stack"
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-primary-400"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Instructeur *</label>
            <input
              type="text"
              value={form.instructor}
              onChange={(e) => update('instructor', e.target.value)}
              placeholder="Votre nom ou celui de l'instructeur"
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-primary-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Catégorie</label>
            <select
              value={form.category}
              onChange={(e) => update('category', e.target.value)}
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 outline-none focus:border-primary-400"
            >
              {courseCategories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Niveau</label>
            <select
              value={form.level}
              onChange={(e) => update('level', e.target.value)}
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 outline-none focus:border-primary-400"
            >
              {courseLevels.map((lv) => (
                <option key={lv} value={lv}>{lv}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Prix (FCFA)</label>
            <input
              type="number"
              min="0"
              value={form.price ?? ''}
              onChange={(e) => update('price', e.target.value !== '' ? Number(e.target.value) : null)}
              placeholder="0 pour gratuit"
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-primary-400"
            />
            <p className="text-xs text-foreground-500 mt-1">Laissez vide ou mettez 0 pour une formation gratuite.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Durée (heures)</label>
            <input
              type="number"
              min="0"
              value={form.duration_hours ?? ''}
              onChange={(e) => update('duration_hours', e.target.value !== '' ? Number(e.target.value) : null)}
              placeholder="32"
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-primary-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Nombre de leçons</label>
            <input
              type="number"
              min="0"
              value={form.lessons_count ?? ''}
              onChange={(e) => update('lessons_count', e.target.value !== '' ? Number(e.target.value) : null)}
              placeholder="48"
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-primary-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Langue</label>
            <select
              value={form.language}
              onChange={(e) => update('language', e.target.value)}
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 outline-none focus:border-primary-400"
            >
              {languages.map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground-400 mb-1.5">Description</label>
          <textarea
            value={form.description}
            onChange={(e) => update('description', e.target.value)}
            rows={5}
            maxLength={2000}
            placeholder="Décrivez le contenu de la formation, le public visé et les bénéfices..."
            className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-primary-400 resize-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground-400 mb-1.5">
            Ce que les apprenants vont acquérir
          </label>
          <textarea
            value={form.objectives}
            onChange={(e) => update('objectives', e.target.value)}
            rows={5}
            maxLength={1000}
            placeholder={'Un objectif par ligne, par exemple :\nMaîtriser les bases de HTML\nCréer une API avec Node.js'}
            className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-primary-400 resize-none"
          />
          <p className="text-xs text-foreground-500 mt-1">Chaque ligne sera affichée comme un objectif distinct.</p>
        </div>
      </section>

      {status === 'error' && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-400/30 text-sm text-red-400">{errorMsg}</div>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="px-6 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors disabled:opacity-60 whitespace-nowrap"
        >
          {saving ? 'Enregistrement...' : existing?.id ? 'Mettre à jour' : 'Publier la formation'}
        </button>
      </div>
    </form>
  );
}

export default CourseForm;